import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { Invoice } from 'contracts/tracking-v2/invoices';
import {
  IndicatorStatusIcon,
  ReturnStatusIcon,
} from 'pages/tracking-v2/shared-components';
import {
  getInvoiceOrigin,
  getInvoiceDestination,
} from 'pages/tracking-v2/utils/invoice-utils';
import {
  DataTableHelper,
  type BuildSelectionOptions,
} from 'utils/data-table-helper';
import { Formatter } from 'utils/Formatter';

type HookOptions = Omit<BuildSelectionOptions, 'size'>

const dt = new DataTableHelper<Invoice>();

export const useInvoiceColumns = (options?: HookOptions): ColumnDef<Invoice>[] => {
  const selection = dt.buildSelectionByPageColumn({ ...options });
  const id = dt.buildColumn({
    id: 'Id',
    accessorKey: 'id',
    size: 64,
    content: (row) => row.id,
  });
  const invoiceNumber = dt.buildColumn({
    id: 'NF',
    accessorKey: 'infnfeIdeNnf',
    size: 64,
    content: (row) => [row.infnfeIdeNnf, row.infnfeIdeSerie].join('-'),
  });
  const emissionDate = dt.buildColumn({
    id: 'Emissão',
    accessorKey: 'infnfeIdeDhemi',
    size: 64,
    content: (row) => Formatter.date(row.infnfeIdeDhemi),
  });
  const deadlineDate = dt.buildColumn({
    id: 'Prazo',
    accessorKey: 'deadlineDate',
    size: 64,
    content: (row) => row.deadlineDate ? Formatter.date(row.deadlineDate) : '---',
  });
  const supplier = dt.buildColumn({
    id: 'Fornecedor',
    accessorFn: (row) => row.supplier.id,
    content: (row) => row?.supplier?.tradeName || '---',
  });
  const company = dt.buildColumn({
    id: 'Centro',
    accessorFn: (row) => row.company.id,
    content: (row) => row?.company?.tradeName || '---',
  });
  const origin = dt.buildColumn({
    id: 'Origem',
    accessorFn: (row) => row.originCity?.id,
    content: (row) => {
      return getInvoiceOrigin(row).asCityAndUfText() || '---';
    },
  });
  const destination = dt.buildColumn({
    id: 'destinationCity.name',
    title: 'Destino',
    accessorFn: (row) => row.destinationCity?.id,
    content: (row) => {
      return getInvoiceDestination(row).asCityAndUfText() || '---';
    },
  });
  const carrier = dt.buildColumn({
    id: 'carrier.tradeName',
    title: 'Transportadora',
    accessorFn: (row) => row.carrier.id,
    content: (row) => row?.carrier?.tradeName || '---',
  });
  const statusIcon = dt.buildColumn({
    id: 'Status',
    title: '',
    enableHiding: false,
    enableSorting: false,
    accessorFn: (row) => row.meta?.indicatorStatus,
    content: (row) => {
      const { meta, returnDate } = row;

      return (
        <div className={'flex items-center justify-end'}>
          {meta?.indicatorStatus && <IndicatorStatusIcon status={meta.indicatorStatus} />}
          {returnDate && <ReturnStatusIcon />}
        </div>
      );
    },
  });

  return [
    selection,
    id,
    invoiceNumber,
    emissionDate,
    deadlineDate,
    supplier,
    company,
    origin,
    destination,
    carrier,
    statusIcon,
  ];
};